import { BrowserProvider } from 'ethers';

let sdk = null;
let provider = null;
let MetaMaskSDKCtor = null;

async function loadMetaMaskSDK() {
  if (MetaMaskSDKCtor) return MetaMaskSDKCtor;

  // Lazy-load to avoid crashing the whole app on initial page load if the SDK (or its
  // transitive deps) has browser-bundling issues. Only needed for mobile flows.
  const mod = await import('@metamask/sdk');
  MetaMaskSDKCtor = mod?.default ?? mod?.MetaMaskSDK ?? mod;
  return MetaMaskSDKCtor;
}

/**
 * Check if the device is mobile
 */
export function isMobileDevice() {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
}

/**
 * Initialize MetaMask SDK for mobile support
 */
export async function initMetaMaskSDK() {
  if (sdk) {
    return sdk;
  }

  try {
    const MetaMaskSDK = await loadMetaMaskSDK();
    sdk = new MetaMaskSDK({
      dappMetadata: {
        name: 'IO Gaming Platform',
        url: window.location.origin,
      },
      injectProvider: false, // Don't inject into window.ethereum
      communicationLayerPreference: 'webrtc',
      shouldShimWeb3: false,
    });

    await sdk.init();
    return sdk;
  } catch (error) {
    console.error('Failed to initialize MetaMask SDK:', error);
    throw error;
  }
}

/**
 * Get MetaMask provider - works on both desktop and mobile
 * Returns window.ethereum if available (desktop), otherwise uses SDK (mobile)
 */
export async function getMetaMaskProvider() {
  // Desktop: use window.ethereum if available
  if (typeof window !== 'undefined' && window.ethereum) {
    return window.ethereum;
  }

  // Mobile: use MetaMask SDK
  if (isMobileDevice()) {
    try {
      if (!sdk) {
        await initMetaMaskSDK();
      }
      
      // Get provider from SDK
      const sdkProvider = sdk.getProvider();
      if (sdkProvider) {
        return sdkProvider;
      }
    } catch (error) {
      console.error('Failed to get MetaMask SDK provider:', error);
    }
  }

  return null;
}

/**
 * Check if MetaMask is available (either extension or mobile app)
 */
export async function isMetaMaskAvailable() {
  // Check desktop extension first
  if (typeof window !== 'undefined' && window.ethereum?.isMetaMask) {
    return true;
  }

  // Check mobile SDK
  if (isMobileDevice()) {
    try {
      if (!sdk) {
        await initMetaMaskSDK();
      }
      const sdkProvider = sdk.getProvider();
      return sdkProvider !== null && sdkProvider !== undefined;
    } catch (error) {
      return false;
    }
  }

  return false;
}

/**
 * Get already-connected accounts without prompting (eth_accounts).
 * Returns accounts if the user has previously connected; empty array otherwise.
 */
export async function getInjectedWalletAccounts() {
  if (typeof window === 'undefined' || !window.ethereum) return [];
  try {
    const accounts = await window.ethereum.request({ method: 'eth_accounts' });
    return Array.isArray(accounts) && accounts.length > 0 ? accounts : [];
  } catch {
    return [];
  }
}

/**
 * Request accounts from MetaMask (works on desktop and mobile)
 */
export async function requestMetaMaskAccounts() {
  const provider = await getMetaMaskProvider();
  
  if (!provider) {
    if (isMobileDevice()) {
      throw new Error(
        'MetaMask is not installed. Please install MetaMask from the Google Play Store (Android) or App Store (iOS) and try again.'
      );
    } else {
      throw new Error('Please install MetaMask to use this payment method');
    }
  }

  try {
    const accounts = await provider.request({ method: 'eth_requestAccounts' });
    return accounts;
  } catch (error) {
    if (error.code === 4001) {
      throw new Error('Please connect your MetaMask wallet to continue');
    }
    throw error;
  }
}

/**
 * Get ethers provider and signer from MetaMask (works on desktop and mobile)
 */
export async function getMetaMaskProviderAndSigner() {
  const provider = await getMetaMaskProvider();
  
  if (!provider) {
    if (isMobileDevice()) {
      throw new Error(
        'MetaMask is not installed. Please install MetaMask from the Google Play Store (Android) or App Store (iOS) and try again.'
      );
    } else {
      throw new Error('MetaMask is not installed');
    }
  }

  // Request accounts first
  await provider.request({ method: 'eth_requestAccounts' });
  
  // Create ethers provider
  const ethersProvider = new BrowserProvider(provider);
  const signer = await ethersProvider.getSigner();
  
  return { provider: ethersProvider, signer, rawProvider: provider };
}
